import React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Stack, SvgIcon, Typography, CardContent, CardActionArea } from '@mui/material';

import { useDocumentActionsStatus } from 'PdfDocumentGrid/hooks/DocumentActionsStatus.context';
import { computeStandardDocumentInfo } from 'PdfDocumentGrid/PdfDocumentsList/helpers';
import DisplayPdfDocumentThumbnail from '../DisplayPdfDocumentThumbnail';
import { IDocumentVersion, IPdfFileMeta } from 'utils/DexieDBManager';
import { RESOURCES_KEYS } from 'utils/i18n/resources';
import { formatDateToFranchDate } from 'utils/utils';

import {
  styledNewPdfFile,
  styledPdfCardLabel,
  styledPdfCardTitle,
  styledValidedPdfFile,
  styledPdfCardContent,
  styledPdfCardValidity,
  styledDownloadedPdfFile,
  styledPdfCardActionAerea,
  
} from './stylesPdfDocumentCards';

import { ReactComponent as CloudCheck } from 'assets/images/cloud-check.svg';
import FontAwesomeSvgIcon from 'Layouts/Common/FontAwesomeSvgIcon';
import svgFonts from 'Layouts/Common/fontAwesome';

interface IPdfDocumentCardActionAreaProps {
  documentVersion: IDocumentVersion;
  documentPdfFileMeta?: IPdfFileMeta;
  isDownloadPdfFileLoading: boolean;
  handleReadPdfDocument: () => void;
}

const PdfDocumentCardActionArea: React.FunctionComponent<IPdfDocumentCardActionAreaProps> = ({
  isDownloadPdfFileLoading,
  documentPdfFileMeta,
  documentVersion,
  handleReadPdfDocument,
}) => {
  const { t: translate } = useTranslation();
  const { state } = useDocumentActionsStatus();

  const foundDocumentStatus = state.documentActionsStatus.get(documentVersion.parentDocument.id);

  const readDate = documentPdfFileMeta?.readDate || documentVersion.uds.lastRead;

  return (
    <CardActionArea onClick={handleReadPdfDocument} disabled={isDownloadPdfFileLoading} sx={styledPdfCardActionAerea}>
      <Stack alignItems='flex-end' spacing={1} sx={styledPdfCardLabel}>
           {/* {foundDocumentStatus?.isUnread !== false && !readDate && (
          <Chip size='small' color='secondary' sx={styledNewPdfFile} label={translate(RESOURCES_KEYS.NEW)} />
        )} */}
        { !readDate && (
          <Chip size='small' color='secondary' sx={styledNewPdfFile} label={translate(RESOURCES_KEYS.NEW)} />
        )}
        {foundDocumentStatus?.isAckRequired !== false && (
          <Chip
            size='small'
            color='error'
            sx={styledValidedPdfFile}
            label={<FontAwesomeSvgIcon icon={svgFonts.faEnvelopeCircleCheck} />}
          />
        )}
        {documentPdfFileMeta?.isBufferSavedInDB && (
          <Chip
            size='small'
            color='primary'
            sx={styledDownloadedPdfFile}
            label={<SvgIcon component={CloudCheck} inheritViewBox />}
          />
        )}
      </Stack>

      <DisplayPdfDocumentThumbnail thumbnailBuffer={documentPdfFileMeta?.thumbnailBuffer} />
      <CardContent sx={styledPdfCardContent}>
        <Typography id='pdfCardTitle' gutterBottom variant='subtitle2' component='div' sx={styledPdfCardTitle}>
          {/* Si le document est normé, on affiche CodeActivite_NumSerieLettre_V1 ou CodeActivite_NumSerie_V1 */}
          {documentVersion.parentDocument.activityCode && (<>{computeStandardDocumentInfo(documentVersion.parentDocument)}{"_V" + documentVersion.version}<br /></>)}
          
          <span dangerouslySetInnerHTML={{ __html: `${documentVersion.title.slice(0, 55)} &#x200B;` }} />
          {/* Si le document n'est pas normé, on affiche le numéro de version en dessous du titre */}
          {documentVersion.parentDocument.activityCode === null && (<span><br />{"V" + documentVersion.version}</span>)}
        </Typography>
        <Typography id='pdfCardValidity' variant='body2' color='text.secondary' sx={styledPdfCardValidity}>
          <span>
            {translate(RESOURCES_KEYS.FROM_DATE)} {formatDateToFranchDate(documentVersion.startDate)}{' '}
          </span>
          {documentVersion.endDate && (
            <span>
              {translate(RESOURCES_KEYS.TO_DATE)} {formatDateToFranchDate(documentVersion.endDate)}
            </span>
          )}
        </Typography>
      </CardContent>
    </CardActionArea>
  );
};

export default PdfDocumentCardActionArea;
